import { Component, inject, OnInit, signal } from '@angular/core';
import { take } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UnderlinedTextareaFieldComponent } from 'src/app/_components/forms/underlined-textarea-field/underlined-textarea-field.component';
import { DeviceMessage } from 'src/app/_models/device';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import { TabOptions } from 'src/app/_models/tab-options';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { DeviceService } from 'src/app/_services/device.service';
import { deviceNavOptions } from 'src/app/_helpers/device-nav-options';
import { RoleCheckPipe } from 'src/app/_helpers/role-check.pipe';
import { ButtonComponent } from 'src/app/_components/buttons/button/button.component';
import { Role } from 'src/app/_models/role';
import { MatIcon } from '@angular/material/icon';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { DatePipe } from '@angular/common';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import { ModalService } from 'src/app/_services/modal.service';
import { UnderlinedOptionTabToggleComponent } from 'src/app/_components/selection-controls/underlined-option-tab-toggle/underlined-option-tab-toggle.component';

@Component({
  selector: 'app-device-messages',
  imports: [
    ReactiveFormsModule,
    UnderlinedTextareaFieldComponent,
    RoleCheckPipe,
    ButtonComponent,
    MatIcon,
    LoadingSpinnerComponent,
    DatePipe,
    ModalComponent,
    UnderlinedOptionTabToggleComponent,
  ],
  templateUrl: './device-messages.component.html',
  styleUrl: './device-messages.component.scss',
})
export class DeviceMessagesComponent implements OnInit {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly deviceService = inject(DeviceService);
  protected formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);

  isLoading = false;
  isProcessing = false;
  submitMessageError: ErrorObject = null;
  getDeviceMessagesError: ErrorObject = null;

  deviceMessages: DeviceMessage[] = [];

  messageMinLength = 1;
  messageMaxLength = 3000;

  deviceId: string;

  // Permissions
  currentUser = this.authenticationService.currentUserValue;
  Role = Role;
  laboratory = signal<Laboratory>(null);

  // Navigation
  navOptions: TabOptions[];

  // Message
  messageField = new FormBase({
    key: 'messageField',
    label: '',
    type: 'textarea',
    placeholder: 'Enter message...',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  messageForm = this.formBuilder.group({
    // General experiment controls
    [this.messageField.key]: [
      '',
      [
        Validators.minLength(this.messageMinLength),
        Validators.maxLength(this.messageMaxLength),
        Validators.required,
      ],
    ],
  });

  ngOnInit(): void {
    this.laboratory.update(() => this.authenticationService.selectedLaboratory);
    this.route.paramMap.pipe(take(1)).subscribe((params) => {
      this.deviceId = params.get('id');
      this.navOptions = deviceNavOptions(this.deviceId, this.currentUser?.role);
      this.getDeviceMessages();
    });
  }

  getDeviceMessages(): void {
    this.getDeviceMessagesError = null;
    this.isLoading = true;

    this.deviceService.getDeviceMessages(this.deviceId).subscribe({
      next: (messages: DeviceMessage[]) => {
        this.deviceMessages = messages;
        this.isLoading = false;
      },
      error: (error: ErrorObject) => {
        this.getDeviceMessagesError = error;
        this.isLoading = false;
      },
    });
  }

  submitDeviceMessage(): void {
    if (this.isProcessing) {
      return;
    }

    this.submitMessageError = null;
    this.isProcessing = true;

    const messageText = this.messageForm.get(this.messageField.key).value;
    const newMessage = new DeviceMessage(messageText);

    this.deviceService
      .submitDeviceMessage(this.deviceId, newMessage)
      .subscribe({
        next: () => {
          this.messageForm.reset();
          this.getDeviceMessages();
          this.isProcessing = false;
          this.modalService.open('success');
        },
        error: (error: ErrorObject) => {
          this.submitMessageError = error;
          this.isProcessing = false;
          this.modalService.open('error');
        },
      });
  }

    modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-button':
        this.modalService.close('error');
        break;
      case 'close-approval':
        this.messageForm.reset();
        this.modalService.close('success');
        break;
    }
  }
}
